import { defaultLocale } from '@/i18n/config';
import { webStorageUtil } from '@/state/webStorage.state.service';
import type { BadgeData } from '@appcharge/shared-ui';
import { parseUtmParams } from '@web-store-platforms/utils';

import { EOfferType, EPlatform, EPlatformType, ESessionStorageKeys } from './enums';
import {
  Gradient,
  OfferData,
  OfferDataForPublisherEvent,
  SequencedProduct,
  SupportedLanguage
} from './types';
import { type ClassValue, clsx } from 'clsx';
import { getLocale } from 'next-intl/server';
import { isMobile, isTablet } from 'react-device-detect';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const extractYouTubeVideoId = (url: string): string | null => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(youtubeRegex);

  return match ? match[1] : null;
};

const extractYouTubePlaylistId = (url: string): string | null => {
  if (!/youtube\.com\/|youtu\.be\//.test(url)) return null;
  const match = url.match(/[?&]list=([^"&?/\s]+)/);

  return match ? match[1] : null;
};

const extractVimeoVideoId = (url: string): string | null => {
  const vimeoRegex = /vimeo\.com\/(?:.*\/)?(\d+)/;
  const match = url.match(vimeoRegex);

  return match ? match[1] : null;
};

export interface VideoEmbedInfo {
  isEmbed: boolean;
  embedUrl: string;
  // When set, the media is a YouTube playlist that should be rendered as a
  // clickable preview linking to the playlist page rather than an inline player
  // (an embedded player can only ever land on the first video).
  playlistUrl?: string;
}

export const getVideoEmbedUrl = (url: string, autoplay: boolean = false): VideoEmbedInfo => {
  if (!url) return { isEmbed: false, embedUrl: url };

  const autoplayParam = autoplay ? '1' : '0';

  const hasYouTubeVideoId = extractYouTubeVideoId(url);
  const hasYouTubePlaylistId = extractYouTubePlaylistId(url);
  if (hasYouTubeVideoId) {
    const playlistParam = hasYouTubePlaylistId ? `&list=${hasYouTubePlaylistId}` : '';
    return {
      isEmbed: true,
      embedUrl: `https://www.youtube.com/embed/${hasYouTubeVideoId}?autoplay=${autoplayParam}&mute=${autoplayParam}${playlistParam}`
    };
  }

  if (hasYouTubePlaylistId) {
    const playlistUrl = `https://www.youtube.com/playlist?list=${hasYouTubePlaylistId}`;
    return {
      isEmbed: true,
      embedUrl: playlistUrl,
      playlistUrl
    };
  }

  const hasVimeoVideoId = extractVimeoVideoId(url);
  if (hasVimeoVideoId) {
    return {
      isEmbed: true,
      embedUrl: `https://player.vimeo.com/video/${hasVimeoVideoId}?autoplay=${autoplayParam}&muted=${autoplayParam}`
    };
  }

  return { isEmbed: false, embedUrl: url };
};

export const getLanguageNameFromIsoCode = (
  isoCode: string | null | undefined,
  languagesList?: SupportedLanguage[],
  fallback?: string
): string => {
  if (!isoCode || !languagesList?.length) return fallback || defaultLocale.language;

  const languageMap = new Map(languagesList.map((lang) => [lang.isoCode, lang.language]));

  return languageMap.get(isoCode) || fallback || 'English';
};

export const getCurrentSubRollingOffer = (offerData: OfferData): SequencedProduct | undefined => {
  const productIndexToCollect = offerData.indexToCollect;

  return offerData?.productsSequence.find(({ index }) => index === productIndexToCollect);
};
export const getSelectedProduct = (
  offerData: OfferData,
  popupIndexToCollect: number
): SequencedProduct | undefined => {
  const productIndexToCollect = offerData.offerType === EOfferType.POP_UP ? popupIndexToCollect : 0;

  if (
    offerData.offerType === EOfferType.ROLLING_OFFER ||
    offerData.offerType === EOfferType.ONE_PLUS_MANY
  ) {
    return getCurrentSubRollingOffer(offerData);
  }

  return offerData?.productsSequence[productIndexToCollect];
};

export const extractOfferMainDetailsV2 = (offers: OfferData[]) => {
  return offers.map((offer: OfferData) => {
    const activeIndex = offer.indexToCollect ? offer.indexToCollect - 1 : 0;
    return {
      id: offer.offerId,
      name: offer.offerName,
      publisherOfferId: offer.publisherOfferId || offer.offerId,
      type: offer.offerType,
      indexToCollect: offer.indexToCollect,
      pricePoint: offer.productsSequence[activeIndex]?.prices?.priceInUsdCents,
      subType: offer.subType,
      priority: offer.priority,
      offerJwt: offer.offerJwt,
      products: offer.productsSequence[activeIndex]?.products?.map((product) => ({
        publisherProductId: product.publisherProductId,
        amount: product.quantity,
      })) ?? [],
    };
  });
};

export const extractOfferMainDetails = (offers: OfferData[]): OfferDataForPublisherEvent[] => {
  return offers.map((offer: OfferData) => ({
    offerId: offer.offerId,
    offerName: offer.offerName,
    publisherOfferId: offer.publisherOfferId || offer.offerId,
    offerType: offer.offerType,
    offerIndex: offer.indexToCollect,
    pricePoint:
      offer.productsSequence[offer.indexToCollect ? offer.indexToCollect - 1 : 0]?.prices
        ?.priceInUsdCents / 100,
    offerSubType: offer.subType,
    offerJwt: offer.offerJwt,
    products: offer.productsSequence[
      offer.indexToCollect ? offer.indexToCollect - 1 : 0
    ].products.map((product) => ({
      publisherProductId: product.publisherProductId,
      amount: product.quantity
    }))
  }));
};

export const extractOfferDetailsForEventsTracking = (offers: OfferData[]) => {
  return offers.map((offer: OfferData) => ({
    id: offer.offerId,
    name: offer.offerName,
    publisherOfferId: offer.publisherOfferId || offer.offerId,
    type: offer.offerType,
    index: offer.indexToCollect,
    pricePoint:
      offer.productsSequence[offer.indexToCollect ? offer.indexToCollect - 1 : 0]?.prices
        ?.priceInUsdCents / 100,
    subType: offer.subType,
    offerJwt: offer.offerJwt,
    products: offer.productsSequence[
      offer.indexToCollect ? offer.indexToCollect - 1 : 0
    ].products.map((product) => ({
      publisherProductId: product.publisherProductId,
      amount: product.quantity
    }))
  }));
};

export const extractRollingOfferMainDetails = (
  offers: OfferData[]
): OfferDataForPublisherEvent[] => {
  return offers.flatMap((offer: OfferData) => {
    const productsSequence = offer.productsSequence.slice(offer.indexToCollect! - 1);

    return productsSequence.map((product: SequencedProduct, index: number) => ({
      offerId: offer.offerId,
      offerName: offer.offerName,
      publisherOfferId: offer.publisherOfferId || offer.offerId,
      offerType: offer.offerType,
      offerIndex: index + offer.indexToCollect!,
      pricePoint: product.prices?.priceInUsdCents / 100,
      offerSubType: offer.subType,
      products: product.products.map((product) => ({
        publisherProductId: product.publisherProductId,
        amount: product.quantity
      }))
    }));
  });
};

export const getUtmParams = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const storedUtmParams = webStorageUtil.get(ESessionStorageKeys.UTM_PARAMS);
  if (storedUtmParams) return storedUtmParams as Record<string, string>;

  const utmData = parseUtmParams(window.location.search, ['utm_source', 'utm_medium', 'utm_campaign']);

  if (Object.keys(utmData).length > 0) {
    webStorageUtil.set(ESessionStorageKeys.UTM_PARAMS, utmData);
    return utmData;
  }

  return {};
};

export const createGradientOrSolid = (color?: Gradient | string) => {
  if (!color) return 'transparent';
  if (typeof color === 'string') {
    return color;
  }

  const { colorOne, colorTwo, gradientDirection } = color;

  if (colorOne && !colorTwo) {
    return colorOne;
  }

  return `linear-gradient(${gradientDirection || 'to right'}, ${colorOne}, ${colorTwo})`;
};

export const formatDate = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
) => {
  return new Date(date).toLocaleDateString('en-US', options);
};

export const mapFontWeight = (weight?: string): number | string => {
  switch (weight) {
    case 'regular':
      return 300;
    case 'medium':
      return 500;
    case 'bold':
      return 700;
    default:
      return weight || 'normal';
  }
};

export const getDeviceData = (
  userAgent: string
): { deviceType: EPlatform; platform: EPlatformType } => {
  const agent = userAgent.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android/.test(agent);

  if (!isMobile) {
    return { deviceType: EPlatform.DESKTOP, platform: EPlatformType.WEB };
  }

  if (/iphone|ipad|ipod/.test(agent)) {
    return { deviceType: EPlatform.MOBILE, platform: EPlatformType.IOS };
  }

  return { deviceType: EPlatform.MOBILE, platform: EPlatformType.ANDROID };
};

export const getButtonStyle = (theme: any) => ({
  color: theme?.loginPopup?.fontColor || '#FFF',
  borderColor: theme?.loginPopup?.fontColor || '#FFF',
  fontWeight: theme?.loginPopup?.fontWeight || 'normal',
  background: '',
  border: '1px solid',
  padding: '10px',
  fontSize: '14px',
  lineHeight: '20px',
  borderRadius: '12px',
  width: '100%',
  marginTop: '16px',
  cursor: 'pointer'
});

export const getBadgeName = (position: string, badges?: BadgeData[]) => {
  const currentBadge = badges?.find((item) => item.position === position);

  return currentBadge?.name ?? '';
};

export const getPlatformData = () => {
  let platform;
  switch (true) {
    case isMobile:
      platform = EPlatform.MOBILE;
      break;
    case isTablet:
      platform = EPlatform.TABLET;
      break;
    default:
      platform = EPlatform.DESKTOP;
  }

  return platform.toLowerCase();
};

const staticPathNameMap: Record<string, string> = {
  '/': 'Home',
  '/support': 'Support',
  '/about': 'About Us'
};

export const getCurrentPageFromPathname = (pathname: string, pages?: Record<string, any>) => {
  if (!pathname || !pages || Object.keys(pages).length === 0) {
    return null;
  }

  const currentPage = Object.entries(pages).find(([, page]) => {
    const pagePath = page.slug === 'home' ? '/' : `/${page.slug}`;

    return pagePath === pathname;
  });

  return currentPage ? currentPage[1] : null;
};

export const getPageNameFromPathname = (path: string, pages?: Record<string, any>): string => {
  if (!path) return '';

  if (staticPathNameMap[path]) {
    return staticPathNameMap[path];
  }

  if (pages && Object.keys(pages).length > 0) {
    const currentPage = getCurrentPageFromPathname(path, pages);

    if (currentPage) {
      return currentPage.pageName;
    }
  }

  const slug = path.replace(/^\//, '');
  if (slug) {
    return slug
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return 'Unknown Page';
};

// Configuration for query parameters to exclude from preservation during navigation
const EXCLUDED_QUERY_PARAMS: string[] = [];

export function preserveQueryParams(
  path: string,
  paramsToExclude: string[] = EXCLUDED_QUERY_PARAMS
): string {
  if (typeof window === 'undefined') return path;

  const currentSearch = window.location.search;
  if (!currentSearch) return path;

  const url = new URL(path, window.location.origin);
  const currentParams = new URLSearchParams(currentSearch);

  // Preserve all query parameters except excluded ones
  currentParams.forEach((value, key) => {
    if (!paramsToExclude.includes(key)) {
      url.searchParams.set(key, value);
    }
  });

  return url.pathname + url.search;
}

export const ensureAbsoluteUrl = (url: string): string => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const DEFAULT_QR_CODE_COLOR = '#000000';
export const QR_CODE_BACKGROUND_COLOR = '#FFFFFF';

export const getQrCodeForegroundColor = (
  qrCodeColor?: string,
  fallbackColor?: string
): string => qrCodeColor ?? fallbackColor ?? DEFAULT_QR_CODE_COLOR;

interface IStoreCatalogOffer {
  offerId?: string;
}

interface IStoreCatalogSections {
  bundles?: IStoreCatalogOffer[];
  popups?: IStoreCatalogOffer[];
  promotions?: IStoreCatalogOffer[];
  rolling?: IStoreCatalogOffer[];
  progressBars?: IStoreCatalogOffer[];
}

export const isOfferInStoreCatalog = (
  offers: IStoreCatalogSections | undefined,
  offerId: string
): boolean => {
  if (!offers) {
    return true;
  }

  const sections = [
    offers.promotions,
    offers.bundles,
    offers.rolling,
    offers.popups,
    offers.progressBars,
  ];

  return sections.some((section) =>
    section?.some((offer) => offer.offerId === offerId)
  );
};

export const getCurrentLocale = async () => {
  try {
    const locale = await getLocale();

    return locale || defaultLocale.isoCode;
  } catch (error) {
    console.warn('Could not get locale from next-intl:', error);

    return defaultLocale.isoCode;
  }
};
